import React, { useState } from 'react';
import { MdChevronLeft, MdChevronRight, MdExpandMore } from 'react-icons/md';

// The credit view of Usage & Billing. Data comes from
// GET /v1/company-usage-billing/credits (the CRM's plan and credit ledger).

export const fmtCredits = (value) => Math.round(Number(value || 0)).toLocaleString();
const fmtPerUnit = (value) => Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 });
const fmtUsd = (value) => `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDay = (value, withYear = false) => (value
  ? new Date(`${value}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC',
  })
  : '—');

const plural = (unit, count) => (Number(count) === 1 ? unit : `${unit}s`);
const creditsText = (value) => `${fmtCredits(value)} ${plural('credit', Math.round(Number(value || 0)))}`;

// Leftover credits still usable this month (oldest first, as the CRM sends them):
// "1,240 left from the Oct 29 cycle · expires Feb 28".
function carriedLotText(lot) {
  const from = lot.source === 'top_up' ? `top-ups in the ${fmtDay(lot.from_month)} cycle` : `the ${fmtDay(lot.from_month)} cycle`;
  return `${fmtCredits(lot.left)} left from ${from}${lot.expires_on ? ` · expires ${fmtDay(lot.expires_on)}` : ''}`;
}

// "Top-ups this month: 2 × $100 (2,000 credits, 640 left)".
function topUpsText(topUps, past = false) {
  const count = Number(topUps.count);
  const each = count > 0 ? Number(topUps.charge_usd || 0) / count : 0;
  return `Top-ups ${past ? 'that month' : 'this month'}: ${count} × ${fmtUsd(each)}`
    + ` (${creditsText(topUps.credits)}, ${fmtCredits(topUps.left)} left)`;
}

export function monthLabel(month) {
  if (!month?.start || !month?.resets_on) return '';
  const last = new Date(`${month.resets_on}T00:00:00Z`);
  last.setUTCDate(last.getUTCDate() - 1);
  return `${fmtDay(month.start)} – ${fmtDay(last.toISOString().slice(0, 10), true)}`;
}

// A past month is final: it says where the month ended, not where it is heading.
const PAST_STATUS = {
  over: { label: 'Used extra credits', tone: 'over' },
  run_out: { label: 'Used extra credits', tone: 'over' },
  close: { label: 'Close to the plan', tone: 'warn' },
  on_track: { label: 'Within the plan', tone: 'good' },
  light: { label: 'Within the plan', tone: 'good' },
  idle: { label: 'No usage', tone: 'quiet' },
};

const STATUS = {
  over: { label: 'Using extra credits', tone: 'over' },
  run_out: { label: 'On pace to run out', tone: 'warn' },
  close: { label: 'Close to your plan', tone: 'warn' },
  on_track: { label: 'On track', tone: 'good' },
  light: { label: 'On track', tone: 'good' },
  idle: { label: 'No usage yet', tone: 'quiet' },
};

const API_GROUPS = [
  { key: 'zoning', label: 'Zoning data' },
  { key: 'listings', label: 'Listings data' },
  { key: 'browser', label: 'Live web research browser' },
  { key: 'images', label: 'Image generation' },
];

// Every group and item, used or not, with this month's units and credits.
export function apiBreakdown(creditData) {
  const used = Object.fromEntries((creditData?.api_groups || []).map((group) => [group.key, group]));
  return API_GROUPS.map((def) => {
    const group = used[def.key];
    const usedItems = Object.fromEntries((group?.items || []).map((item) => [item.key, item]));
    const items = (creditData?.api_units || [])
      .filter((unit) => unit.group === def.key)
      .map((unit) => ({ ...unit, units: usedItems[unit.key]?.units || 0, credits: usedItems[unit.key]?.credits || 0 }));
    (group?.items || []).forEach((item) => {
      if (!items.some((known) => known.key === item.key)) items.push(item);
    });
    return { ...def, label: group?.label || def.label, credits: group?.credits || 0, items };
  });
}

function Expandable({ title, hint, value, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`usage-credits-expand${open ? ' is-open' : ''}`}>
      <button type="button" className="usage-credits-expand-head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <MdExpandMore className="usage-credits-expand-icon" size={18} />
        <span className="usage-credits-expand-title">
          <strong>{title}</strong>
          {hint && <span>{hint}</span>}
        </span>
        <span className="usage-credits-expand-value">{value}</span>
      </button>
      {open && <div className="usage-credits-expand-body">{children}</div>}
    </div>
  );
}

function ItemLine({ item, showUsage, hideLabel = false }) {
  const perUnit = item.credits_per_unit;
  const rate = perUnit == null
    ? 'credits follow session length'
    : `${fmtPerUnit(perUnit)} ${plural('credit', perUnit)} per ${item.unit}`;
  return (
    <li className={`usage-credits-item${hideLabel ? ' no-label' : ''}`}>
      {!hideLabel && <span className="usage-credits-item-label">{item.label}</span>}
      <span className="usage-credits-item-rate">
        {showUsage ? `${fmtCredits(item.units)} ${plural(item.unit, item.units)} · ${rate}` : rate}
      </span>
      {showUsage && !hideLabel && <span className="usage-credits-item-credits">{fmtCredits(item.credits)}</span>}
    </li>
  );
}

const fmtApprox = (value) => (value == null || value === '' ? '—' : `~${fmtCredits(value)}`);
const fmtRange = (low, high) => (low == null || high == null ? '—' : `${fmtCredits(low)}–${fmtCredits(high)}`);

export function AtlasExamples({ examples }) {
  const rows = examples || [];
  return (
    <>
      <p className="usage-credits-body-intro">
        What a whole piece of work costs, measured from recent use: every message back and forth, plus any data
        pulled along the way. Typical is the middle conversation, most fall in the range shown, and big ones are
        the top 10%.
      </p>
      <div className="usage-credits-metrics-wrap">
        <table className="usage-credits-metrics">
          <thead>
            <tr>
              <th scope="col">Use case</th>
              <th scope="col">Messages</th>
              <th scope="col">Typical</th>
              <th scope="col">Most</th>
              <th scope="col">Big ones</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <th scope="row">
                  <strong>{row.label}</strong>
                  {row.detail && <span>{row.detail}</span>}
                  {row.note && <span className="usage-credits-example-note">{row.note}</span>}
                </th>
                <td>{row.messages || '—'}</td>
                <td className="typical">{fmtApprox(row.typical)}</td>
                <td>{fmtRange(row.low, row.high)}</td>
                <td>{row.big != null ? `${fmtCredits(row.big)}+` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="usage-credits-metrics-unit">All figures in credits per conversation.</p>
    </>
  );
}

export function ApiCosts({ creditData, showUsage }) {
  const groups = apiBreakdown(creditData);
  return (
    <>
      <p className="usage-credits-body-intro">
        {showUsage
          ? 'Paid data Atlas pulled for your team this month. Each ping is a request for one record or one set of results.'
          : 'Paid data Atlas pulls for you is charged per ping, at these rates.'}
      </p>
      <div className="usage-credits-groups">
        {groups.map((group) => (
          <div className="usage-credits-group" key={group.key}>
            <div className="usage-credits-group-head">
              <strong>{group.label}</strong>
              {showUsage && <span>{creditsText(group.credits)}</span>}
            </div>
            <ul>
              {group.items.map((item) => (
                <ItemLine
                  key={item.key}
                  item={item}
                  showUsage={showUsage}
                  hideLabel={group.items.length === 1 && item.label === group.label}
                />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </>
  );
}

export function CreditPeriodHeader({ creditData, onPrevious = null, onNext = null }) {
  const { plan = {}, month = {} } = creditData;
  const past = month.is_current === false;
  // No arrows until there is another month to show (customers see only months billed in credits).
  const canPage = Boolean(onPrevious || onNext);
  return (
    <div className="usage-billing-period-nav">
      {canPage && (
        <button type="button" aria-label="Previous month" disabled={!onPrevious} onClick={onPrevious || undefined}>
          <MdChevronLeft size={18} />
        </button>
      )}
      <div className="usage-billing-period-center">
        <div className="usage-billing-period-kicker">
          {past ? 'Past month · ' : ''}{plan.name ? `${plan.name} plan` : 'Your plan'}{plan.interval ? ` · billed ${plan.interval}` : ''}
        </div>
        <div className="usage-billing-period-label">{monthLabel(month)}</div>
        <div className="usage-billing-period-sub">
          {month.of > 1 ? `Month ${month.index} of ${month.of} · ` : ''}
          {past ? `Closed ${fmtDay(month.resets_on, true)}` : `Credits reset ${fmtDay(month.resets_on, true)}`}
        </div>
      </div>
      {canPage && (
        <button type="button" aria-label="Next month" disabled={!onNext} onClick={onNext || undefined}>
          <MdChevronRight size={18} />
        </button>
      )}
    </div>
  );
}

export function CreditSummary({ creditData, personalOnly }) {
  const credits = creditData.credits || {};
  const plan = creditData.plan || {};
  const preview = creditData.billing_model !== 'credits';
  const me = personalOnly ? (creditData.members || [])[0] : null;

  if (personalOnly) {
    return (
      <div className="usage-credits-card">
        <div className="usage-credits-headline">
          <span>{creditData.month?.is_current === false ? 'Your credits that month' : 'Your credits this month'}</span>
          <strong>{fmtCredits(me?.credits)}</strong>
        </div>
        <div className="usage-credits-legend">
          <span><i className="atlas" />Atlas {fmtCredits(me?.atlas_credits)}</span>
          <span><i className="api" />Data {fmtCredits(me?.api_credits)}</span>
        </div>
      </div>
    );
  }

  const available = Math.max(Number(credits.available || 0), 1);
  const atlasPct = Math.min(100, (Number(credits.atlas || 0) / available) * 100);
  const apiPct = Math.min(100 - atlasPct, (Number(credits.api || 0) / available) * 100);
  const past = creditData.month?.is_current === false;
  const status = (past ? PAST_STATUS : STATUS)[credits.status] || null;
  const carried = (Array.isArray(credits.carried) ? credits.carried : []).filter((lot) => Number(lot?.left) > 0);
  const added = [
    `${fmtCredits(credits.monthly)} ${creditData.month?.is_current === false ? 'for the month' : 'this month'}`,
    Number(credits.carried_in) > 0 ? `${fmtCredits(credits.carried_in)} carried over` : null,
    Number(credits.loaded) > 0 ? `${fmtCredits(credits.loaded)} added by RealTorch` : null,
  ].filter(Boolean);
  const blockNote = plan.extra_billing === 'per_credit'
    ? `${fmtUsd(plan.extra_block_price_usd)} per ${fmtCredits(plan.extra_block_credits)} credits`
    : `blocks of ${fmtCredits(plan.extra_block_credits)} for ${fmtUsd(plan.extra_block_price_usd)}`;

  return (
    <div className="usage-credits-card">
      {preview && (
        <div className="usage-credits-preview">
          Credit billing isn&apos;t active for your account yet. This shows how your usage counts against your plan.
        </div>
      )}
      <div className="usage-credits-headline">
        <span>Credits used</span>
        <strong>{fmtCredits(credits.used)} <small>of {fmtCredits(credits.available)}</small></strong>
        {status && (
          <span className={`usage-credits-status ${status.tone}`}>
            {status.label}{credits.status === 'run_out' && credits.run_out_on ? ` ${fmtDay(credits.run_out_on)}` : ''}
          </span>
        )}
      </div>
      <div className="usage-credits-bar" role="img"
           aria-label={`${fmtCredits(credits.atlas)} Atlas credits and ${fmtCredits(credits.api)} data credits used of ${fmtCredits(credits.available)}`}>
        <span className="atlas" style={{ width: `${atlasPct}%` }} />
        <span className="api" style={{ width: `${apiPct}%` }} />
      </div>
      <div className="usage-credits-legend">
        <span><i className="atlas" />Atlas {fmtCredits(credits.atlas)}</span>
        <span><i className="api" />Data {fmtCredits(credits.api)}</span>
        <span className="usage-credits-remaining">
          {!past ? `${fmtCredits(credits.remaining)} remaining`
            : plan.carryover && Number(credits.carry_out_so_far) > 0 ? `${fmtCredits(credits.carry_out_so_far)} carried into the next month`
              : `${fmtCredits(credits.remaining)} unused`}
        </span>
      </div>
      <div className="usage-credits-sub">{added.join(' + ')}</div>
      {carried.length > 0 && (
        <div className="usage-credits-carried">
          <ul aria-label="Leftover credits">
            {carried.map((lot, index) => (
              <li key={`${lot.from_month}-${lot.source}-${index}`}>{carriedLotText(lot)}</li>
            ))}
          </ul>
          <div className="usage-credits-carried-note">
            {past ? 'That month' : 'This month'}&apos;s credits are used first, then leftovers, oldest first.
          </div>
        </div>
      )}
      {Number(credits.top_ups?.count) > 0 && (
        <div className="usage-credits-topups">{topUpsText(credits.top_ups, past)}</div>
      )}
      {Number(credits.extra) > 0 && (
        <div className="usage-credits-extra">
          {fmtCredits(credits.extra)} extra credits {past ? 'that month' : 'this month'}
          {Number(credits.extra_charge_usd) > 0 ? ` · ${fmtUsd(credits.extra_charge_usd)}${preview ? ' if billed' : ''}` : ''}
          <span> (extra credits come in {blockNote})</span>
        </div>
      )}
      {!past && Number(credits.extra) <= 0 && Number(credits.projected_extra) > 0 && (
        <div className="usage-credits-projection">
          At this pace you&apos;ll use about {fmtCredits(credits.projected)} credits by {fmtDay(creditData.month?.resets_on)},
          {' '}about {fmtCredits(credits.projected_extra)} more than your plan includes
          {Number(credits.projected_extra_usd) > 0 ? ` (≈ ${fmtUsd(credits.projected_extra_usd)} in extra credits${preview ? ' if billed' : ''})` : ''}.
        </div>
      )}
    </div>
  );
}

export function CreditBreakdown({ creditData, personalOnly }) {
  const credits = creditData.credits || {};
  const me = personalOnly ? (creditData.members || [])[0] : null;
  const atlas = personalOnly ? me?.atlas_credits : credits.atlas;
  const api = personalOnly ? me?.api_credits : credits.api;
  return (
    <div className="usage-credits-breakdown">
      <Expandable
        title="Atlas cost (credits)"
        hint="The AI's work on each message: reading, thinking, writing and using tools"
        value={creditsText(atlas)}
      >
        <AtlasExamples examples={creditData.examples} />
      </Expandable>
      <Expandable
        title="Data costs"
        hint="Paid data: zoning, listings, live web research and images"
        value={creditsText(api)}
      >
        <ApiCosts creditData={creditData} showUsage={!personalOnly} />
      </Expandable>
    </div>
  );
}

export function creditFootnote(creditData) {
  const plan = creditData?.plan || {};
  if (plan.monthly_credits == null) return 'Atlas messages and paid data pulls both use your company\'s plan credits.';
  const carryoverMonths = Number(creditData?.credits?.carryover_months || 0);
  const carry = carryoverMonths > 0
    ? `Credits you don't use stay usable for ${carryoverMonths} more ${plural('month', carryoverMonths)}; each month uses its own credits first, then leftovers, oldest first.`
    : plan.carryover
      ? 'Credits you don\'t use carry into the next month only, and each month uses its own credits first.'
      : 'Unused credits reset each month.';
  return `Your plan includes ${fmtCredits(plan.monthly_credits)} credits a month. ${carry} Atlas messages and paid data pulls both use credits.`;
}

// The daily chart's input, in credits.
export function creditChartData(creditData, members) {
  return {
    members,
    daily_by_member: (creditData?.days || []).map((day) => ({
      date: day.date,
      members: Object.entries(day.by_member || {}).map(([userId, credits]) => ({ user_id: Number(userId), amount: credits })),
    })),
    period: {
      start: creditData?.month?.start,
      end: creditData?.month?.resets_on,
      is_current: true,
      label: monthLabel(creditData?.month),
    },
  };
}
